import subscriberDeliveryZones from './mocks/json/subscriber-delivery-zones.json';

/** Visual accent for the live preview (Tailwind utility strings). */
export type TemplateAccent = 'neutral' | 'amber' | 'cyan' | 'rose';

export interface TemplatePreviewTheme {
  card: string;
  header: string;
  subject: string;
  fromMuted: string;
  tagBadge: string;
  kickerBadge: string;
  bulletBorder: string;
  /** Small circle in the template list */
  swatch: string;
}

export function getTemplatePreviewTheme(accent: TemplateAccent): TemplatePreviewTheme {
  switch (accent) {
    case 'amber':
      return {
        card: 'border-amber-200/90 bg-white shadow-md shadow-amber-100/40 ring-1 ring-amber-100/60',
        header:
          'border-b border-amber-200/80 bg-gradient-to-br from-amber-50 via-orange-50/90 to-amber-100/40',
        subject: 'text-amber-950',
        fromMuted: 'text-amber-900/75',
        tagBadge: 'border-amber-300/70 bg-amber-50/80 text-amber-950',
        kickerBadge: 'border-0 bg-amber-600 text-white hover:bg-amber-600',
        bulletBorder: 'border-amber-400',
        swatch: 'bg-amber-500 shadow-sm shadow-amber-200',
      };
    case 'cyan':
      return {
        card: 'border-cyan-200/90 bg-white shadow-md shadow-cyan-100/40 ring-1 ring-cyan-100/50',
        header: 'border-b border-cyan-200/80 bg-gradient-to-br from-cyan-50 via-sky-50/80 to-cyan-100/35',
        subject: 'text-cyan-950',
        fromMuted: 'text-cyan-900/75',
        tagBadge: 'border-cyan-300/70 bg-cyan-50/80 text-cyan-950',
        kickerBadge: 'border-0 bg-cyan-600 text-white hover:bg-cyan-600',
        bulletBorder: 'border-cyan-500',
        swatch: 'bg-cyan-500 shadow-sm shadow-cyan-200',
      };
    case 'rose':
      return {
        card: 'border-rose-200/90 bg-white shadow-md shadow-rose-100/40 ring-1 ring-rose-100/50',
        header: 'border-b border-rose-200/80 bg-gradient-to-br from-rose-50 via-orange-50/40 to-rose-100/35',
        subject: 'text-rose-950',
        fromMuted: 'text-rose-900/75',
        tagBadge: 'border-rose-300/70 bg-rose-50/80 text-rose-950',
        kickerBadge: 'border-0 bg-rose-600 text-white hover:bg-rose-600',
        bulletBorder: 'border-rose-400',
        swatch: 'bg-rose-500 shadow-sm shadow-rose-200',
      };
    case 'neutral':
    default:
      return {
        card: 'border-white/10 bg-slate-950/45 backdrop-blur-xl shadow-[0_8px_28px_rgba(0,0,0,0.35)]',
        header: 'border-b border-white/5 bg-white',
        subject: 'text-slate-100',
        fromMuted: 'text-slate-400',
        tagBadge: 'border-white/10 text-slate-200 bg-white',
        kickerBadge: '',
        bulletBorder: 'border-blue-100',
        swatch: 'bg-gray-400',
      };
  }
}

export type DeliveryZoneId = 'de' | 'vn';

export const DELIVERY_ZONE_META: Record<
  DeliveryZoneId,
  { label: string; iana: string; hint: string }
> = subscriberDeliveryZones as Record<DeliveryZoneId, { label: string; iana: string; hint: string }>;

/** IANA options for moderator subscriber scheduling (extends subscriber prefs zones). */
export const MODERATOR_SUBSCRIBER_TIMEZONES: { value: string; label: string }[] = [
  { value: 'Europe/Berlin', label: 'Germany — Europe/Berlin' },
  { value: 'Asia/Ho_Chi_Minh', label: 'Vietnam — Asia/Ho_Chi_Minh' },
  { value: 'UTC', label: 'UTC' },
  { value: 'Europe/London', label: 'UK — Europe/London' },
  { value: 'America/New_York', label: 'US Eastern — America/New_York' },
];

/** Half-hour slots HH:mm for selects (local wall time in chosen zone). */
export const SEND_TIME_OPTIONS: string[] = Array.from({ length: 48 }, (_, i) => {
  const h = Math.floor(i / 2);
  const m = (i % 2) * 30;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
});
